import { useEffect, useRef } from 'react';
import gsap from 'gsap';

const TRAIL = 7; // number of ghost dots trailing the cursor

const CustomCursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const trailRefs = useRef([]);
  const rippleRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia('(hover: none)').matches) return;

    document.body.classList.add('custom-cursor-active');

    let mouseX = -300, mouseY = -300;
    let ringX = -300, ringY = -300;
    const trailPos = Array.from({ length: TRAIL }, () => ({ x: -300, y: -300 }));

    // ── Instant dot tracking ───────────────────────────────────────────────────
    const onMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      gsap.to(dotRef.current, {
        x: mouseX - 4,
        y: mouseY - 4,
        duration: 0.04,
        ease: 'none',
      });
    };

    // ── Per-frame: ring lag + trail cascade ────────────────────────────────────
    const tick = () => {
      // Ring follows with smooth lag
      ringX += (mouseX - ringX - 20) * 0.11;
      ringY += (mouseY - ringY - 20) * 0.11;
      gsap.set(ringRef.current, { x: ringX, y: ringY });

      // Each trail dot follows the one before it with decreasing speed
      trailPos[0].x += (mouseX - 4 - trailPos[0].x) * 0.5;
      trailPos[0].y += (mouseY - 4 - trailPos[0].y) * 0.5;
      for (let i = 1; i < TRAIL; i++) {
        const speed = Math.max(0.1, 0.42 - i * 0.05);
        trailPos[i].x += (trailPos[i - 1].x - trailPos[i].x) * speed;
        trailPos[i].y += (trailPos[i - 1].y - trailPos[i].y) * speed;
      }
      trailPos.forEach((p, i) => {
        const el = trailRefs.current[i];
        if (!el) return;
        const size = Math.max(1.5, 6.5 - i * 0.7);
        gsap.set(el, {
          x: p.x,
          y: p.y,
          width: size,
          height: size,
          opacity: (1 - (i + 1) / (TRAIL + 2)) * 0.52,
        });
      });
    };

    // ── Click ripple burst ─────────────────────────────────────────────────────
    const onMouseDown = (e) => {
      if (!rippleRef.current) return;
      gsap.killTweensOf(rippleRef.current);
      gsap.set(rippleRef.current, {
        x: e.clientX - 20,
        y: e.clientY - 20,
        scale: 0.15,
        opacity: 0.8,
        display: 'block',
      });
      gsap.to(rippleRef.current, {
        scale: 3.2,
        opacity: 0,
        duration: 0.58,
        ease: 'power2.out',
      });
    };

    // ── Hover: ring expands + dot shrinks ─────────────────────────────────────
    const onEnter = () => {
      gsap.to(ringRef.current, {
        scale: 2.5,
        opacity: 0.32,
        duration: 0.28,
        ease: 'power2.out',
      });
      gsap.to(dotRef.current, { scale: 0.3, duration: 0.25 });
    };

    const onLeave = () => {
      gsap.to(ringRef.current, {
        scale: 1,
        opacity: 1,
        duration: 0.48,
        ease: 'elastic.out(1, 0.55)',
      });
      gsap.to(dotRef.current, { scale: 1, duration: 0.35 });
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);
    gsap.ticker.add(tick);

    const interactables = document.querySelectorAll(
      'a, button, [role="button"], input, label, select, textarea, [tabindex]'
    );
    interactables.forEach((el) => {
      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mouseleave', onLeave);
    });

    return () => {
      document.body.classList.remove('custom-cursor-active');
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      gsap.ticker.remove(tick);
      interactables.forEach((el) => {
        el.removeEventListener('mouseenter', onEnter);
        el.removeEventListener('mouseleave', onLeave);
      });
    };
  }, []);

  return (
    <>
      {/* Main dot — instant */}
      <div
        ref={dotRef}
        style={{
          position: 'fixed', top: 0, left: 0,
          width: 8, height: 8,
          borderRadius: '50%',
          background: '#159895',
          pointerEvents: 'none',
          zIndex: 999999,
          willChange: 'transform',
        }}
      />

      {/* Lagging ring */}
      <div
        ref={ringRef}
        style={{
          position: 'fixed', top: 0, left: 0,
          width: 40, height: 40,
          borderRadius: '50%',
          border: '1.5px solid #159895',
          pointerEvents: 'none',
          zIndex: 999999,
          willChange: 'transform',
        }}
      />

      {/* Comet trail ghosts */}
      {Array.from({ length: TRAIL }, (_, i) => (
        <div
          key={i}
          ref={(el) => (trailRefs.current[i] = el)}
          style={{
            position: 'fixed', top: 0, left: 0,
            width: 6, height: 6,
            borderRadius: '50%',
            background: '#159895',
            pointerEvents: 'none',
            zIndex: 999998,
            willChange: 'transform',
            opacity: 0,
          }}
        />
      ))}

      {/* Click ripple ring */}
      <div
        ref={rippleRef}
        style={{
          position: 'fixed', top: 0, left: 0,
          width: 40, height: 40,
          borderRadius: '50%',
          border: '1.5px solid #159895',
          pointerEvents: 'none',
          zIndex: 999997,
          willChange: 'transform',
          display: 'none',
        }}
      />
    </>
  );
};

export default CustomCursor;
